import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery } from 'convex/react'
import {
  Search,
  ExternalLink,
  Globe,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  AlertTriangle,
  Send,
  CheckCircle2,
  Clock3,
  XCircle,
  Ban,
  EyeOff,
  Undo2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/contexts/auth-context'
import { api } from '@/../convex/_generated/api'
import { type Directory, type DirectoryStatus, type DirectoryFlag, type PricingModel } from './data'
import { SITE_HOST } from '@/lib/config/site'

const STATUS_STYLES: Record<DirectoryStatus, { label: string; className: string; dot: string; icon: typeof CheckCircle2 }> = {
  accepted: { label: 'Live', className: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20', dot: 'bg-emerald-500', icon: CheckCircle2 },
  submitted: { label: 'In review', className: 'bg-amber-50 text-amber-700 ring-amber-600/20', dot: 'bg-amber-500', icon: Clock3 },
  rejected: { label: 'Rejected', className: 'bg-rose-50 text-rose-700 ring-rose-600/20', dot: 'bg-rose-500', icon: XCircle },
  pending: { label: 'Not submitted', className: 'bg-neutral-100 text-neutral-500 ring-neutral-300/40', dot: 'bg-neutral-300', icon: Send },
}

const FLAG_STYLES: Record<DirectoryFlag, { label: string; className: string }> = {
  'non-directory': { label: 'Not a directory', className: 'bg-rose-50 text-rose-700 ring-rose-600/20' },
  duplicate: { label: 'Duplicate', className: 'bg-orange-50 text-orange-700 ring-orange-600/20' },
  'low-quality': { label: 'Low quality', className: 'bg-neutral-100 text-neutral-500 ring-neutral-300/40' },
  review: { label: 'Review fit', className: 'bg-amber-50 text-amber-700 ring-amber-600/20' },
  ineligible: { label: 'Not eligible', className: 'bg-rose-50 text-rose-700 ring-rose-600/20' },
  defunct: { label: 'Defunct', className: 'bg-neutral-100 text-neutral-600 ring-neutral-400/40' },
}

/** Format a cost number as a dollar string for the UI. */
function formatCost(d: Directory): string {
  if (d.cost == null) return '—'
  if (d.cost === 0) return 'Free'
  const cad = d.costCadence === 'monthly' ? '/mo' : d.costCadence === 'yearly' ? '/yr' : ''
  return `$${d.cost.toLocaleString()}${cad}`
}

const PRICING_STYLES: Record<PricingModel, { label: string; className: string }> = {
  free: { label: 'Free', className: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20' },
  freemium: { label: 'Freemium', className: 'bg-sky-50 text-sky-700 ring-sky-600/20' },
  paid: { label: 'Paid', className: 'bg-amber-50 text-amber-700 ring-amber-600/20' },
  'editorial-pitch': { label: 'Editorial', className: 'bg-violet-50 text-violet-700 ring-violet-600/20' },
  'auto-scrape': { label: 'Auto-claim', className: 'bg-neutral-50 text-neutral-700 ring-neutral-300/40' },
}

/** Considered "viable" — actionable, not blocked by eligibility, defunct, etc. */
function isViable(d: Directory): boolean {
  if (d.eligibility && d.eligibility.eligible === false) return false
  if (d.flag === 'ineligible' || d.flag === 'defunct' || d.flag === 'non-directory') return false
  return true
}

const STATUS_ORDER: DirectoryStatus[] = ['accepted', 'submitted', 'pending', 'rejected']

type SortKey = 'domainRating' | 'name' | 'category' | 'status' | 'priority' | 'cost'
type SortDir = 'asc' | 'desc'

function dofollowBadge(value: string) {
  const v = (value || '').toLowerCase()
  if (v === 'yes' || v === 'dofollow') return { label: 'Dofollow', className: 'bg-sky-50 text-sky-700 ring-sky-600/20' }
  if (v === 'nofollow') return { label: 'Nofollow', className: 'bg-neutral-100 text-neutral-500 ring-neutral-300/40' }
  return { label: 'Unknown', className: 'bg-neutral-50 text-neutral-400 ring-neutral-200/60' }
}

function drColor(dr: number): string {
  if (dr >= 80) return 'text-emerald-700'
  if (dr >= 65) return 'text-emerald-600'
  if (dr >= 50) return 'text-amber-600'
  return 'text-neutral-500'
}

function isDofollow(d: Directory): boolean {
  const v = (d.dofollow || '').toLowerCase()
  return v === 'yes' || v === 'dofollow'
}

/**
 * Higher = better. Used to rank the "Up next" queue. Combines DR, dofollow
 * confidence, presence of a submission URL, and a penalty for flagged or
 * non-pending entries.
 */
function priorityScore(d: Directory): number {
  if (d.status !== 'pending') return -1
  if (!isViable(d)) return -1
  const dr = d.domainRating || 0
  const dofollow = isDofollow(d) ? 18 : 0
  const url = d.submissionUrl ? 6 : 0
  const requirementsReady = d.requirements && d.requirements.length > 0 ? 3 : 0 // known requirements = lower friction
  const flagPenalty = d.flag === 'review' ? 6 : 0
  return dr + dofollow + url + requirementsReady - flagPenalty
}

export default function AdminDirectoriesPage() {
  const { user, isAuthenticated } = useAuth()
  const isAdmin = isAuthenticated && user?.isAdmin

  // Source of truth — the Convex `directories` table. Returns undefined
  // while loading; treat as empty so derived counts stay sane during paint.
  const directoriesQuery = useQuery(api.directories.list, isAdmin ? {} : 'skip')
  const DIRECTORIES: Directory[] = useMemo(() => (directoriesQuery ?? []) as Directory[], [directoriesQuery])

  // Per-admin skip overrides (Convex-backed, persists across devices)
  const overrides = useQuery(api.directoryOverrides.listMine, isAdmin ? {} : 'skip')
  const setSkipped = useMutation(api.directoryOverrides.setSkipped)
  const skippedSet = useMemo(() => {
    const m = new Map<string, { skipped: boolean; skipReason?: string }>()
    for (const o of overrides || []) m.set(o.slug, { skipped: o.skipped, skipReason: o.skipReason })
    return m
  }, [overrides])
  const isUserSkipped = (slug: string) => skippedSet.get(slug)?.skipped === true

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | DirectoryStatus>('all')
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null)
  const [hideFlagged, setHideFlagged] = useState(true)
  const [sortKey, setSortKey] = useState<SortKey>('priority')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  const categories = useMemo(() => {
    const counts = new Map<string, number>()
    for (const d of DIRECTORIES) counts.set(d.category, (counts.get(d.category) || 0) + 1)
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1])
  }, [DIRECTORIES])

  const stats = useMemo(() => {
    const s = {
      total: DIRECTORIES.length,
      accepted: 0,
      submitted: 0,
      rejected: 0,
      pending: 0,
      flagged: 0,
      ineligible: 0,
      viablePending: 0, // pending AND eligible AND no defunct/non-directory flag AND not user-skipped
      dofollowLive: 0,  // accepted with dofollow → real link equity
      userSkipped: 0,
    }
    for (const d of DIRECTORIES) {
      s[d.status]++
      if (d.flag) s.flagged++
      if (d.eligibility?.eligible === false || ['ineligible', 'defunct', 'non-directory'].includes(d.flag || '')) {
        s.ineligible++
      }
      if (isUserSkipped(d.slug)) s.userSkipped++
      if (d.status === 'pending' && isViable(d) && !isUserSkipped(d.slug)) s.viablePending++
      if (d.status === 'accepted' && isDofollow(d)) s.dofollowLive++
    }
    return s
  }, [DIRECTORIES, skippedSet])

  const filtered = useMemo(() => {
    let result = DIRECTORIES.slice()
    // "Hide flagged" hides ineligible / defunct / non-directory entries AND
    // anything the current admin has manually skipped.
    if (hideFlagged) result = result.filter((d) => !d.flag && isViable(d) && !isUserSkipped(d.slug))
    if (search) {
      const q = search.toLowerCase()
      result = result.filter(
        (d) =>
          d.target.toLowerCase().includes(q) ||
          d.name.toLowerCase().includes(q) ||
          d.category.toLowerCase().includes(q) ||
          (d.description || '').toLowerCase().includes(q) ||
          (d.fee || '').toLowerCase().includes(q)
      )
    }
    if (statusFilter !== 'all') result = result.filter((d) => d.status === statusFilter)
    if (categoryFilter) result = result.filter((d) => d.category === categoryFilter)

    const dir = sortDir === 'asc' ? 1 : -1
    result.sort((a, b) => {
      switch (sortKey) {
        case 'domainRating':
          return ((a.domainRating || 0) - (b.domainRating || 0)) * dir
        case 'name':
          return a.name.localeCompare(b.name) * dir
        case 'category':
          return a.category.localeCompare(b.category) * dir
        case 'status':
          return (STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status)) * dir
        case 'priority':
          return (priorityScore(a) - priorityScore(b)) * dir
        case 'cost':
          // Treat undefined cost as -1 so it falls to the bottom when sorting desc.
          return ((a.cost ?? -1) - (b.cost ?? -1)) * dir
        default:
          return 0
      }
    })
    return result
  }, [DIRECTORIES, search, statusFilter, categoryFilter, hideFlagged, sortKey, sortDir, skippedSet])

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir(key === 'name' || key === 'category' ? 'asc' : 'desc')
    }
  }

  const SortIcon = ({ k }: { k: SortKey }) => {
    if (sortKey !== k) return <ArrowUpDown className="h-3 w-3 opacity-40" />
    return sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
  }

  if (!isAdmin) {
    return (
      <div className="p-6">
        <p className="text-sm text-neutral-500">Admin access required.</p>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <header className="space-y-3">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Backlink tracker</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Goal: grow {SITE_HOST} DR by listing on every directory that will accept us. Skip the rest — geographic locks,
            B2B-only platforms, dead sites, and paid-link schemes are flagged so they don't clutter the action queue.
          </p>
        </div>
        <HeroStats stats={stats} />
      </header>

      <Pipeline stats={stats} active={statusFilter} onSelect={setStatusFilter} />

      <SkippedByMe
        skipped={DIRECTORIES.filter((d) => isUserSkipped(d.slug))}
        onUnskip={(slug) => setSkipped({ slug, skipped: false })}
      />

      <section className="space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-sm font-semibold text-neutral-900">
            Full backlog
            {filtered.length !== stats.total && (
              <span className="ml-2 text-neutral-500 font-normal">({filtered.length} of {stats.total})</span>
            )}
          </h2>
          <span className="text-xs text-neutral-400">
            Default view hides ineligible / defunct / paid-link / non-directory entries. Toggle below to see them.
          </span>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative max-w-sm flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <input
              type="text"
              placeholder="Search name, target, description, fee…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-neutral-200 bg-white text-neutral-900 placeholder:text-neutral-400 outline-none focus:border-neutral-400 focus:ring-2 focus:ring-neutral-100"
            />
          </div>

          <select
            value={categoryFilter || ''}
            onChange={(e) => setCategoryFilter(e.target.value || null)}
            className="text-sm px-3 py-2 rounded-lg border border-neutral-200 bg-white text-neutral-700 outline-none focus:border-neutral-400"
          >
            <option value="">All categories</option>
            {categories.map(([cat, count]) => (
              <option key={cat} value={cat}>
                {cat} ({count})
              </option>
            ))}
          </select>

          <label className="inline-flex items-center gap-2 text-sm text-neutral-600 px-3 py-2 rounded-lg border border-neutral-200 bg-white cursor-pointer hover:border-neutral-400">
            <input
              type="checkbox"
              checked={hideFlagged}
              onChange={(e) => setHideFlagged(e.target.checked)}
              className="accent-neutral-900"
            />
            Hide skipped / ineligible ({stats.ineligible + stats.userSkipped})
          </label>
        </div>

        <div className="border border-neutral-200 rounded-xl overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-neutral-50 border-b border-neutral-200 text-xs font-medium text-neutral-500 uppercase tracking-wider">
                <SortableTh label="DR" k="domainRating" toggle={toggleSort} icon={<SortIcon k="domainRating" />} className="w-16 text-right pr-4" />
                <SortableTh label="Directory" k="name" toggle={toggleSort} icon={<SortIcon k="name" />} />
                <SortableTh label="Category" k="category" toggle={toggleSort} icon={<SortIcon k="category" />} className="w-32" />
                <th className="text-left px-4 py-3 w-20">Link</th>
                <SortableTh label="Cost" k="cost" toggle={toggleSort} icon={<SortIcon k="cost" />} className="w-24 text-right" />
                <SortableTh label="Priority" k="priority" toggle={toggleSort} icon={<SortIcon k="priority" />} className="w-20 text-right" />
                <SortableTh label="Status" k="status" toggle={toggleSort} icon={<SortIcon k="status" />} className="w-40" />
                <th className="text-left px-4 py-3 w-16"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => (
                <DirectoryRow
                  key={d.slug}
                  d={d}
                  userSkipped={isUserSkipped(d.slug)}
                  onSkip={() => setSkipped({ slug: d.slug, skipped: true })}
                  onUnskip={() => setSkipped({ slug: d.slug, skipped: false })}
                />
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-neutral-400">
                    <Globe className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p>No directories match your filters</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}

function HeroStats({
  stats,
}: {
  stats: {
    total: number
    accepted: number
    submitted: number
    pending: number
    flagged: number
    ineligible: number
    viablePending: number
    dofollowLive: number
    userSkipped: number
  }
}) {
  const cells: Array<{ label: string; value: number; tone: string; hint?: string }> = [
    {
      label: 'Live listings',
      value: stats.accepted,
      tone: 'text-emerald-700',
      hint: `${stats.dofollowLive} pass dofollow link equity`,
    },
    {
      label: 'Ready to submit',
      value: stats.viablePending,
      tone: 'text-sky-700',
      hint: 'Eligible, not yet submitted — the action queue',
    },
    {
      label: 'In review',
      value: stats.submitted,
      tone: 'text-amber-700',
      hint: 'Submitted, awaiting acceptance',
    },
    {
      label: 'Skipped',
      value: stats.ineligible + stats.userSkipped,
      tone: 'text-neutral-500',
      hint: `${stats.ineligible} auto (ineligible/defunct) + ${stats.userSkipped} skipped by you`,
    },
  ]
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {cells.map((c) => (
        <div key={c.label} className="rounded-xl border border-neutral-200 bg-white p-4">
          <div className="text-[11px] uppercase tracking-wide text-neutral-500">{c.label}</div>
          <div className={cn('text-2xl font-bold tabular-nums mt-1', c.tone)}>{c.value}</div>
          {c.hint && <div className="text-[11px] text-neutral-400 mt-1.5 leading-tight">{c.hint}</div>}
        </div>
      ))}
    </div>
  )
}

function Pipeline({
  stats,
  active,
  onSelect,
}: {
  stats: { total: number; accepted: number; submitted: number; rejected: number; pending: number; flagged: number }
  active: 'all' | DirectoryStatus
  onSelect: (s: 'all' | DirectoryStatus) => void
}) {
  const segments: Array<{ key: 'all' | DirectoryStatus; label: string; value: number; tone: string; activeTone: string }> = [
    { key: 'pending', label: 'Backlog', value: stats.pending, tone: 'bg-neutral-50 text-neutral-700 hover:bg-neutral-100', activeTone: 'bg-neutral-900 text-white' },
    { key: 'submitted', label: 'Submitted', value: stats.submitted, tone: 'bg-amber-50 text-amber-700 hover:bg-amber-100', activeTone: 'bg-amber-600 text-white' },
    { key: 'accepted', label: 'Accepted', value: stats.accepted, tone: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100', activeTone: 'bg-emerald-600 text-white' },
    { key: 'rejected', label: 'Rejected', value: stats.rejected, tone: 'bg-rose-50 text-rose-700 hover:bg-rose-100', activeTone: 'bg-rose-600 text-white' },
  ]
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button
        onClick={() => onSelect('all')}
        className={cn(
          'px-3 py-2 rounded-lg text-sm font-medium transition-colors ring-1 ring-inset',
          active === 'all'
            ? 'bg-neutral-900 text-white ring-neutral-900'
            : 'bg-white text-neutral-700 ring-neutral-200 hover:ring-neutral-300'
        )}
      >
        All <span className="tabular-nums opacity-70 ml-1">{stats.total}</span>
      </button>
      <div className="text-neutral-300 text-sm select-none">›</div>
      {segments.map((s, i) => (
        <div key={s.key} className="flex items-center gap-2">
          <button
            onClick={() => onSelect(s.key)}
            className={cn(
              'px-3 py-2 rounded-lg text-sm font-medium transition-colors',
              active === s.key ? s.activeTone : s.tone
            )}
          >
            {s.label} <span className="tabular-nums opacity-80 ml-1">{s.value}</span>
          </button>
          {i < segments.length - 1 && <span className="text-neutral-300 text-sm select-none">›</span>}
        </div>
      ))}
    </div>
  )
}

function SkippedByMe({
  skipped,
  onUnskip,
}: {
  skipped: Directory[]
  onUnskip: (slug: string) => void
}) {
  const [expanded, setExpanded] = useState(false)
  if (skipped.length === 0) return null
  return (
    <section className="rounded-xl border border-neutral-200 bg-white">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-neutral-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <EyeOff className="h-4 w-4 text-neutral-500" />
          <span className="text-sm font-semibold text-neutral-900">Skipped by you</span>
          <span className="text-xs text-neutral-500">({skipped.length})</span>
        </div>
        <span className="text-xs text-neutral-400">{expanded ? 'Hide' : 'Show'}</span>
      </button>
      {expanded && (
        <ol className="border-t border-neutral-200 divide-y divide-neutral-100">
          {skipped.map((d) => (
            <li key={d.slug} className="flex items-center gap-3 px-4 py-2.5 text-sm">
              <span className={cn('text-xs font-semibold tabular-nums w-10 text-right', drColor(d.domainRating))}>
                {d.domainRating}
              </span>
              <Link to={`/admin/directories/${d.slug}`} className="font-medium text-neutral-900 hover:underline decoration-dotted underline-offset-2">
                {d.name}
              </Link>
              <span className="text-xs text-neutral-400 truncate">{d.target}</span>
              <button
                onClick={() => onUnskip(d.slug)}
                className="ml-auto inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md text-emerald-700 hover:bg-emerald-50"
              >
                <Undo2 className="h-3.5 w-3.5" />
                Unskip — put back in queue
              </button>
            </li>
          ))}
        </ol>
      )}
    </section>
  )
}

function SortableTh({
  label,
  k,
  toggle,
  icon,
  className,
}: {
  label: string
  k: SortKey
  toggle: (k: SortKey) => void
  icon: React.ReactNode
  className?: string
}) {
  return (
    <th className={cn('text-left px-4 py-3', className)}>
      <button
        onClick={() => toggle(k)}
        className="inline-flex items-center gap-1.5 font-medium uppercase tracking-wider text-xs text-neutral-500 hover:text-neutral-900 transition-colors"
      >
        {label}
        {icon}
      </button>
    </th>
  )
}

function DirectoryRow({
  d,
  userSkipped,
  onSkip,
  onUnskip,
}: {
  d: Directory
  userSkipped: boolean
  onSkip: () => void
  onUnskip: () => void
}) {
  const status = STATUS_STYLES[d.status]
  const follow = dofollowBadge(d.dofollow)
  const flag = d.flag ? FLAG_STYLES[d.flag] : null
  const pricing = d.pricingModel ? PRICING_STYLES[d.pricingModel] : null
  const score = priorityScore(d)
  const isLive = d.status === 'accepted'
  const isIneligible = d.eligibility?.eligible === false
  return (
    <tr
      className={cn(
        'border-b border-neutral-100 transition-colors group',
        userSkipped
          ? 'bg-neutral-100/50 opacity-60'
          : isIneligible
            ? 'bg-rose-50/30 opacity-70'
            : d.flag
              ? 'bg-neutral-50/40'
              : isLive
                ? 'bg-emerald-50/30 hover:bg-emerald-50/50'
                : 'hover:bg-neutral-50/60'
      )}
    >
      <td className={cn('px-4 py-2.5 text-right pr-4 tabular-nums font-semibold', drColor(d.domainRating))}>
        {d.domainRating}
      </td>
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to={`/admin/directories/${d.slug}`}
            className="font-medium text-neutral-900 hover:text-neutral-600 hover:underline decoration-dotted underline-offset-2"
          >
            {d.name}
          </Link>
          <span className="text-xs text-neutral-400">{d.target}</span>
          {userSkipped && (
            <span
              className="text-[10px] font-medium px-1.5 py-0.5 rounded ring-1 inline-flex items-center gap-1 bg-neutral-200 text-neutral-700 ring-neutral-400/40"
              title="You manually skipped this. Click the row's Unskip button to put it back in the queue."
            >
              <EyeOff className="h-2.5 w-2.5" />
              Skipped by you
            </span>
          )}
          {isIneligible && !userSkipped && (
            <span
              className="text-[10px] font-medium px-1.5 py-0.5 rounded ring-1 inline-flex items-center gap-1 bg-rose-50 text-rose-700 ring-rose-600/20"
              title={d.eligibility?.reason || 'Not eligible'}
            >
              <Ban className="h-2.5 w-2.5" />
              Skip
            </span>
          )}
          {flag && !isIneligible && !userSkipped && (
            <span className={cn('text-[10px] font-medium px-1.5 py-0.5 rounded ring-1 inline-flex items-center gap-1', flag.className)}>
              <AlertTriangle className="h-2.5 w-2.5" />
              {flag.label}
            </span>
          )}
          {d.profileUrl && isLive && (
            <a
              href={d.profileUrl}
              onClick={(e) => e.stopPropagation()}
              target="_blank"
              rel="noreferrer noopener"
              title="Footage profile on this directory"
              className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-1.5 py-0.5 rounded ring-1 ring-emerald-600/20 transition-colors"
            >
              Open profile <ExternalLink className="h-3 w-3" />
            </a>
          )}
          {pricing && (
            <span className={cn('text-[10px] font-medium px-1.5 py-0.5 rounded ring-1', pricing.className)}>
              {pricing.label}
            </span>
          )}
        </div>
        {(isIneligible ? d.eligibility?.reason : d.description) && (
          <div className={cn('text-xs mt-1 line-clamp-1', isIneligible ? 'text-rose-700/80' : 'text-neutral-500')}>
            {isIneligible ? d.eligibility?.reason : d.description}
          </div>
        )}
      </td>
      <td className="px-4 py-2.5">
        <span className="text-xs px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-600">{d.category}</span>
      </td>
      <td className="px-4 py-2.5">
        <span className={cn('text-[11px] font-medium px-1.5 py-0.5 rounded ring-1', follow.className)}>
          {follow.label}
        </span>
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums text-xs">
        <span className={d.cost && d.cost > 0 ? 'text-amber-700 font-medium' : 'text-neutral-500'}>
          {formatCost(d)}
        </span>
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums text-xs text-neutral-500">
        {score > 0 ? score.toFixed(0) : '—'}
      </td>
      <td className="px-4 py-2.5">
        <span className={cn('inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full ring-1', status.className)}>
          <span className={cn('h-1.5 w-1.5 rounded-full', status.dot)} />
          {status.label}
        </span>
      </td>
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-1.5">
          {userSkipped ? (
            <button
              onClick={onUnskip}
              className="text-neutral-500 hover:text-emerald-700 transition-colors p-1.5 rounded hover:bg-emerald-50"
              title="Put back in queue"
            >
              <Undo2 className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={onSkip}
              className="text-neutral-300 hover:text-rose-600 transition-colors p-1.5 rounded hover:bg-rose-50"
              title="Skip this directory (hidden from your queue until you unskip)"
            >
              <EyeOff className="h-4 w-4" />
            </button>
          )}
          <Link
            to={`/admin/directories/${d.slug}`}
            className="text-neutral-300 hover:text-neutral-700 transition-colors p-1.5"
            title="Open detail"
          >
            <ArrowUpDown className="h-3.5 w-3.5 rotate-90" />
          </Link>
        </div>
      </td>
    </tr>
  )
}
